// Colourway swatches on the watch product page — thumbnails of the sibling
// products in this watch's colourway group (same custom.title_internal
// metafield, falling back to the visible title when blank), rendered below
// the price. Clicking a swatch swaps the page in place, Nike-style: the
// sibling's PDP is fetched and its gallery/details are transplanted into
// the current page, the URL is pushed, and the swatch row itself is kept
// exactly as-is (same order) with the highlight moved. The
// `.js-pdp-title-swatches` container in layouts/product.edge stays hidden
// unless the product is a watch with at least one in-stock sibling.
import algoliasearch from 'algoliasearch/lite';
import { stardust } from '@eidellev/adonis-stardust/client';
import { getIndexName } from '../../../../app/Libraries/Algolia/AlgoliaUtils';
import { getSizedShopifyImageUrl, getPageLang } from '../utils';
import {
  getInternalTitle,
  getGroupTitle,
  getGroupKey,
  isSwatchHit,
  isInStock,
} from '../title-swatch-utils';

// Only bind popstate once a swap has rewritten the URL — reloading is the
// simplest way to make back/forward land on the page the URL says it is
let popstateBound = false;

// Cache of sibling PDP documents so a colourway is fetched at most once —
// primed by prefetch-on-hover so most clicks resolve instantly. A rejected
// fetch is evicted so it can be retried.
const pageCache = new Map<string, Promise<Document>>();
const fetchSiblingDoc = (url: string): Promise<Document> => {
  let doc = pageCache.get(url);
  if (!doc) {
    // ?fragment=1 tells the server to skip the "you may also like" query — the
    // swap only uses the gallery + details columns anyway
    const fetchUrl = `${url}${url.includes('?') ? '&' : '?'}fragment=1`;
    doc = fetch(fetchUrl, { headers: { Accept: 'text/html' } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'))
      .catch((err) => {
        pageCache.delete(url);
        throw err;
      });
    pageCache.set(url, doc);
  }
  return doc;
};

/**
 * Nike-style in-place colourway swap: fetch the sibling's product page,
 * transplant its gallery column + details column into the current page
 * (keeping this swatch row exactly as it is), swap the per-product
 * drop-a-hint modal, update the URL/title, and let the product modules
 * re-bind to the fresh DOM via the 'product:swap' event. Falls back to a
 * normal navigation if anything unexpected happens.
 */
const swapToSibling = async (link: HTMLAnchorElement, container: HTMLElement) => {
  const url = link.href;

  // Visual feedback while the (possibly still-loading) sibling page resolves
  container.classList.add('title-swatches--swapping');
  link.classList.add('title-swatches__swatch--loading');

  try {
    // Keep the cached document intact: the transplant consumes its child nodes.
    const doc = (await fetchSiblingDoc(url)).cloneNode(true) as Document;

    const newProduct = doc.querySelector<HTMLElement>('.js-product');
    const currentProduct = document.querySelector<HTMLElement>('.js-product');
    const newGalleryColumn = doc.querySelector('.single-product__gallery')?.parentElement;
    const currentGalleryColumn = document.querySelector('.single-product__gallery')?.parentElement;
    const newDetails = doc.querySelector('.single-product__details');
    const currentDetails = document.querySelector('.single-product__details');
    if (!newProduct || !currentProduct || !newGalleryColumn || !currentGalleryColumn
      || !newDetails || !currentDetails) {
      throw new Error('Unexpected product page markup');
    }

    // Keep this swatch row (same group, same order): move the highlight to
    // the clicked colourway, then put the live row where the fetched page's
    // empty placeholder sits so it survives the details transplant
    container.querySelectorAll('.title-swatches__swatch').forEach((swatch) => {
      swatch.classList.remove('title-swatches__swatch--selected');
      swatch.removeAttribute('aria-current');
    });
    link.classList.add('title-swatches__swatch--selected');
    link.setAttribute('aria-current', 'page');
    newDetails.querySelector('.js-pdp-title-swatches')?.replaceWith(container);

    currentGalleryColumn.replaceChildren(...newGalleryColumn.childNodes);
    currentDetails.replaceChildren(...newDetails.childNodes);

    // Product-level data attributes drive the cart/analytics/affirm modules
    ['data-product', 'data-price-hidden', 'data-product-type'].forEach((attr) => {
      const value = newProduct.getAttribute(attr);
      if (value === null) {
        currentProduct.removeAttribute(attr);
      } else {
        currentProduct.setAttribute(attr, value);
      }
    });

    // The drop-a-hint modal lives outside .js-product and is per-product
    const newModal = doc.querySelector('#drop-a-hint-modal');
    const currentModal = document.querySelector('#drop-a-hint-modal');
    if (currentModal && newModal) {
      currentModal.replaceWith(newModal);
    } else if (currentModal) {
      currentModal.remove();
    } else if (newModal) {
      document.body.appendChild(newModal);
    }

    document.title = doc.title;
    window.history.pushState({ titleSwatchSwap: true }, '', url);
    if (!popstateBound) {
      popstateBound = true;
      window.addEventListener('popstate', () => window.location.reload());
    }

    // Let every product module re-bind to the fresh DOM
    document.dispatchEvent(new CustomEvent('product:swap'));
    document.dispatchEvent(new CustomEvent('accordion:add'));

    container.classList.remove('title-swatches--swapping');
    // the live row was transplanted into the new details; clear the per-link
    // loading marker on whichever swatch carried it
    container.querySelector('.title-swatches__swatch--loading')?.classList.remove('title-swatches__swatch--loading');
  } catch {
    // Graceful fallback: navigate like the plain link it is
    window.location.href = url;
  }
};

document.addEventListener('DOMContentLoaded', async () => {
  const container = document.querySelector<HTMLElement>('.js-pdp-title-swatches');
  const productEl = document.querySelector<HTMLElement>('.js-product');
  const handle = productEl?.dataset.product;
  if (!container || !handle) return;

  const searchClient = algoliasearch(
    process.env.ALGOLIA_APP_ID as string,
    process.env.ALGOLIA_SEARCH_KEY as string,
  );
  const indexName = getIndexName(getPageLang(), 'featured', 'asc');
  const RETRIEVE = ['title', 'handle', 'image', 'meta', 'product_type', 'inventory_available'];
  const baseParams = {
    hitsPerPage: 50,
    attributesToRetrieve: RETRIEVE,
    analytics: false,
    clickAnalytics: false,
    // unlisted products (private client links) share a title with their
    // public original — never surface them as colourway siblings
    filters: 'NOT tags:"inquiry-only"',
  };

  try {
    // This product's Algolia record is the source of its internal title
    const { results } = await searchClient.search([{
      indexName,
      query: handle,
      params: { ...baseParams, restrictSearchableAttributes: ['handle'] },
    }] as any);
    const hit = ((results[0] as any).hits || [])
      .find((h: any) => h.handle === handle);
    if (!hit || !isSwatchHit(hit) || !getGroupTitle(hit)) return;

    // Fetch the whole colourway group — curated groups match on the
    // meta.custom.title_internal facet; fallback groups on the title text
    const { results: groupResults } = await searchClient.search([
      getInternalTitle(hit)
        ? {
          indexName,
          query: '',
          params: {
            ...baseParams,
            facetFilters: [[`meta.custom.title_internal:${getInternalTitle(hit)}`]],
          },
        }
        : {
          indexName,
          query: String(hit.title || ''),
          params: { ...baseParams, restrictSearchableAttributes: ['title'] },
        },
    ] as any);

    // Exact matches within the same group kind, with inventory — but the
    // product being viewed always shows, even if it's out of stock
    let siblings = (((groupResults[0] as any).hits) || []).filter((sibling: any) =>
      getGroupKey(sibling) === getGroupKey(hit)
      && (isInStock(sibling) || sibling.handle === handle));
    if (!siblings.some((sibling: any) => sibling.handle === handle)) {
      siblings = [hit, ...siblings];
    }
    if (siblings.length < 2) return;

    // The current product leads the row
    siblings = [
      ...siblings.filter((sibling: any) => sibling.handle === handle),
      ...siblings.filter((sibling: any) => sibling.handle !== handle),
    ];

    const swatches = siblings
      .map((sibling: any) => {
        const url = stardust.route(`${getPageLang()}.shopify.slug`, {
          slug: sibling.handle,
        }, { qs: { v: sibling.objectID, oid: sibling.objectID } });

        const selected = sibling.handle === handle;
        const thumb = sibling.image
          ? getSizedShopifyImageUrl(sibling.image, 200)
          : '/img/placeholder.png';

        return `
          <a class="title-swatches__swatch${selected ? ' title-swatches__swatch--selected' : ''}"
            href="${url}"${selected ? ' aria-current="page"' : ''}
            aria-label="${String(sibling.title || '').replace(/"/g, '&quot;')}"
          >
            <img src="${thumb}" alt="" loading="lazy" />
          </a>`;
      })
      .join('');

    container.innerHTML = `<div class="title-swatches__track">${swatches}</div>`;
    container.hidden = false;

    // Prefetch a colourway's page as soon as the user hovers/focuses its
    // swatch, so the click almost always resolves from cache (delegated)
    const prefetch = (e: Event) => {
      const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('.title-swatches__swatch');
      if (!link || link.classList.contains('title-swatches__swatch--selected')) return;
      fetchSiblingDoc(link.href).catch(() => { /* click will fall back to navigation */ });
    };
    container.addEventListener('pointerover', prefetch);
    container.addEventListener('focusin', prefetch);

    // Swap in place instead of navigating (delegated — the row's swatch
    // elements survive every swap, so this binds exactly once)
    container.addEventListener('click', (e) => {
      const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('.title-swatches__swatch');
      if (!link) return;
      e.preventDefault();
      if (link.classList.contains('title-swatches__swatch--selected')) return;
      swapToSibling(link, container);
    });
  } catch {
    // Algolia unavailable — the container just stays hidden
  }
});
